import React, { useState } from 'react';
import { ArrowRight, Play, Sparkles, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Button } from '../ui/button';
import { stats, teamPerformance } from '../../data/mock';
import { getOptimizedImageUrl } from '../../lib/utils';

const HeroSection = () => {
  const [currentTeamIndex, setCurrentTeamIndex] = useState(0);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  const nextTeam = () => {
    setCurrentTeamIndex((prev) => (prev + 1) % teamPerformance.length);
  };

  const prevTeam = () => {
    setCurrentTeamIndex((prev) => (prev - 1 + teamPerformance.length) % teamPerformance.length);
  };

  const currentTeam = teamPerformance[currentTeamIndex];
  const imageSrc = currentTeam.homepageSliderOverrideImage || currentTeam.image;

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-cream via-pragmatic/10 to-richolive/5 pt-20">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-pragmatic/15 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-richolive/10 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSA2MCAwIEwgMCAwIDAgNjAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iI2MzYTY5MzIwIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-40" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-pragmatic/30 to-richolive/20 border border-dwarfpony/30 rounded-full px-4 py-2 mb-6">
              <Sparkles size={16} className="text-dwarfpony" />
              <span className="text-dwarfpony text-sm font-medium">India's FIRST Global Partner</span>
            </div>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-deepwalnut leading-tight mb-6">
              Promoting STEM Education Through
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-dwarfpony to-dwarfpony-dark">
                Real-World Learning
              </span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg md:text-xl text-heavyrain leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0">
              We help you participate in the Global Robotics Olympics competition – <span className="text-richolive font-medium">FIRST Global</span>
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
              <a href="https://forms.gle/zB5NBG3P1YmJJM6U9" target="_blank" rel="noopener noreferrer">
                <Button
                  data-testid="hero-apply-btn"
                  className="bg-gradient-to-r from-dwarfpony to-dwarfpony-dark hover:from-dwarfpony-dark hover:to-dwarfpony text-white font-semibold px-8 py-6 text-lg shadow-xl shadow-dwarfpony/25 hover:shadow-dwarfpony/40 transition-all group"
                >
                  Apply Now for FGC 2027
                  <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </a>
              <Button
                onClick={() => setIsVideoModalOpen(true)}
                variant="outline"
                className="border-deepwalnut/30 text-deepwalnut hover:bg-pragmatic/20 px-6 py-6 text-lg group"
              >
                <Play size={20} className="mr-2 group-hover:scale-110 transition-transform" />
                Watch Video
              </Button>
            </div>

            {/* Video Modal */}
            {isVideoModalOpen && (
              <div className="fixed inset-0 z-50 bg-midnight/80 backdrop-blur-sm flex items-center justify-center p-4">
                <div className="relative w-full max-w-4xl aspect-video rounded-2xl overflow-hidden bg-black shadow-2xl">
                  <iframe
                    src="https://www.youtube.com/embed/jAmBNhRHE90?autoplay=1"
                    title="FIRST Global Video"
                    allow="autoplay; encrypted-media"
                    allowFullScreen
                    className="w-full h-full border-0"
                  />
                  <button
                    onClick={() => setIsVideoModalOpen(false)}
                    className="absolute top-4 right-4 w-10 h-10 bg-cream/90 hover:bg-cream text-deepwalnut rounded-full flex items-center justify-center shadow-lg transition-colors z-10"
                    aria-label="Close video"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12 pt-8 border-t border-pragmatic/30">
              <div className="text-center lg:text-left">
                <p className="text-3xl md:text-4xl font-bold text-deepwalnut">{stats.yearsActive}</p>
                <p className="text-heavyrain text-sm mt-1">Years Active</p>
              </div>
              <div className="text-center lg:text-left">
                <p className="text-3xl md:text-4xl font-bold text-deepwalnut">{stats.studentsEngaged}</p>
                <p className="text-heavyrain text-sm mt-1">Students Engaged</p>
              </div>
              <div className="text-center lg:text-left">
                <p className="text-3xl md:text-4xl font-bold text-deepwalnut">{stats.industryExperts}</p>
                <p className="text-heavyrain text-sm mt-1">Industry Experts</p>
              </div>
              <div className="text-center lg:text-left">
                <p className="text-3xl md:text-4xl font-bold text-deepwalnut">{stats.foundersSupported}</p>
                <p className="text-heavyrain text-sm mt-1">Founders</p>
              </div>
            </div>
          </div>

          {/* Right Content - Robot Illustration */}
          <div className="relative">
            <div className="relative aspect-square max-w-lg mx-auto">
              {/* Glowing Circle */}
              <div className="absolute inset-0 bg-gradient-to-br from-pragmatic/30 via-cream to-richolive/20 rounded-full blur-2xl animate-pulse" />

              {/* Robot Placeholder */}
              <div className="relative w-full h-full rounded-3xl bg-cream border border-pragmatic/30 shadow-2xl overflow-hidden group">
                <img
                  key={currentTeam.year}
                  src={getOptimizedImageUrl(imageSrc, 1200)}
                  srcSet={`
                    ${getOptimizedImageUrl(imageSrc, 400)} 400w,
                    ${getOptimizedImageUrl(imageSrc, 800)} 800w,
                    ${getOptimizedImageUrl(imageSrc, 1200)} 1200w
                  `}
                  sizes="(max-width: 768px) 100vw, 50vw"
                  alt={`Team India ${currentTeam.year} at FIRST Global`}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500 animate-in fade-in zoom-in duration-500"
                  style={{ objectPosition: currentTeam.imagePosition || 'center center' }}
                  loading="eager"
                />

                {/* Light Overlay for better text visibility */}
                <div className="absolute inset-0 bg-gradient-to-t from-midnight/40 via-transparent to-transparent" />

                {/* Floating Badge */}
                <div className="absolute top-6 right-6 bg-cream/80 backdrop-blur-md rounded-xl px-4 py-3 border border-pragmatic/30 shadow-lg transition-all">
                  <p className="text-dwarfpony font-bold text-lg">{currentTeam.year}</p>
                  <p className="text-heavyrain text-xs">{currentTeam.problemStatement}</p>
                </div>

                {/* Bottom Badge */}
                <div className="absolute bottom-6 left-6 right-6 bg-cream/80 backdrop-blur-md rounded-xl px-4 py-3 border border-pragmatic/30 shadow-lg transition-all">
                  <p className="text-deepwalnut font-medium">{currentTeam.location}</p>
                  <p className="text-heavyrain text-sm">FIRST Global {currentTeam.year} Finals</p>
                </div>

                {/* Navigation Arrows */}
                <div className="absolute inset-y-0 left-0 flex items-center px-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <button
                    onClick={prevTeam}
                    className="p-2 rounded-full bg-cream/80 hover:bg-cream backdrop-blur text-deepwalnut shadow-lg transition-all border border-pragmatic/20"
                    aria-label="Previous Team"
                  >
                    <ChevronLeft size={20} />
                  </button>
                </div>
                <div className="absolute inset-y-0 right-0 flex items-center px-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <button
                    onClick={nextTeam}
                    className="p-2 rounded-full bg-cream/80 hover:bg-cream backdrop-blur text-deepwalnut shadow-lg transition-all border border-pragmatic/20"
                    aria-label="Next Team"
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-heavyrain text-xs uppercase tracking-widest">Scroll</span>
        <div className="w-6 h-10 border-2 border-pragmatic rounded-full flex justify-center pt-2">
          <div className="w-1 h-2 bg-dwarfpony rounded-full" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
